import { Button, ClickAwayListener, styled } from '@ethings/ui-toolkit';
import { Box, Theme, useTheme } from '@mui/material';
import { SxProps } from '@mui/system';
import chroma from 'chroma-js';
import { FC, useEffect, useState } from 'react';
import { ColorResult, TwitterPicker } from 'react-color';

interface Props {
  'data-testid'?: string;
  color?: string;
  colors?: string[];
  disabled?: boolean;
  label?: string;
  onChange?: (color: string) => void;
  sx?: SxProps<Theme>;
}

const StyledButton = styled(Button)(({ theme }) => ({
  ':focus, :focus-within': {
    borderColor: theme.palette.primary.main,
  },
  ':hover:not(:focus)': {
    borderColor: theme.palette.common.black,
  },
  background: theme.palette.background.paper,
  border: `${chroma(theme.palette.common.black).alpha(0.25).css()} .25px solid`,
  borderRadius: `${theme.shape.borderRadius}px`,
  cursor: 'pointer',
  display: 'inline-block',
  minWidth: '30px',
  padding: '8px',
}));

const DEFAULT_LENGTH = 7;
export const DEFAULT_COLORS = [
  '#202020',
  '#808080',
  '#DFDFDF',
  '#FF0000',
  '#DC143C',
  '#FFA500',
  '#FFD700',
  '#00FF00',
  '#7FFF00',
  '#9ACD32',
  '#228B22',
  '#0000FF',
  '#00FFFF',
  '#20B2AA',
  '#4682B4',
  '#00BFFF',
  '#8A2BE2',
  '#6A5ACD',
  '#9400D3',
  '#C71585',
];

export const ColorPicker: FC<Props> = ({
  color,
  colors = DEFAULT_COLORS,
  disabled,
  label,
  onChange,
  sx,
  ...props
}) => {
  const [displayPicker, setDisplayPicker] = useState<boolean>(false);
  const [selectedColor, setSelectedColor] = useState<string>(color ?? colors[0] ?? '');
  const theme = useTheme();

  const styles = {
    default: {
      hash: {
        display: 'none',
      },
      input: {
        display: 'none',
      },
    },
  };

  const handleClick = (): void => {
    setDisplayPicker(!displayPicker);
  };

  const handleChange = (newColor: ColorResult): void => {
    setSelectedColor(newColor.hex);
    onChange?.(newColor.hex);
    setDisplayPicker(false);
  };

  useEffect(() => {
    if (!color) return;
    setSelectedColor(color);
  }, [color]);

  return (
    <Box sx={{ position: 'relative', ...sx }}>
      <StyledButton
        {...props}
        title={label}
        color="inherit"
        disabled={disabled}
        disableFocusRipple
        disableRipple
        onClick={handleClick}
        fullWidth
      >
        <Box
          sx={{
            background: selectedColor,
            borderRadius: `${theme.shape.borderRadius}px`,
            height: '24px',
            transition: 'background 100ms linear',
            width: '100%',
          }}
        />
      </StyledButton>
      {displayPicker
        && <ClickAwayListener onClickAway={() => setDisplayPicker(false)}>
          <Box data-testid="color-picker-popup" sx={{ position: 'absolute', bottom: '-10px', transform: 'translateY(100%)', zIndex: 1000 }}>
            <TwitterPicker
              colors={colors}
              onChange={handleChange}
              styles={styles}
              width={colors.length < DEFAULT_LENGTH ? `${colors.length * 36 + 24}px` : '276px'}
            />
          </Box>
        </ClickAwayListener>
      }
    </Box>
  );
};
